import {
  BrandLogo,
  ButtonV2,
  Heading,
  Icon,
  Text,
  VerticalSpace,
} from '@arc-ui/components';
import { useState, useMemo, KeyboardEvent } from 'react';
import { MenuItem, NavigationPath } from '../../../types/sidebar';
import {
  NavigationData,
  Column,
  LinkGroup,
  NavigationItem,
} from '../../../types/ee-header';
import { CHEVRON_LEFT_ICON, CHEVRON_RIGHT_ICON } from '../../atoms/SVG';
import { useHeaderContext } from '../../../contexts';
import { UtilityLinks } from '../UtilityLinks/UtilityLinks';

type EnrichedItem = MenuItem & { supporting?: string };

const NestedMenu = () => {
  const {
    nav,
    personalSegmentLinkUrl,
    personalSegmentTitle,
    businessSegmentLargeBusinessButtonLabel,
    businessSegmentLargeBusinessLinkUrl,
    businessSegmentLargeBusinessMessage,
    businessSegmentLargeBusinessTitle,
    businessSegmentSmallBusinessLinkUrl,
    businessSegmentSmallBusinessMessage,
    businessSegmentSmallBusinessTitle,
  } = useHeaderContext();
  const [navigationStack, setNavigationStack] = useState<NavigationPath[]>([]);

  const mainMenuItems = useMemo(() => {
    const processLinkListItems = (
      linkListItems: Record<string, unknown>,
    ): Record<string, MenuItem> => {
      const converted: Record<string, MenuItem> = {};
      Object.entries(linkListItems).forEach(([itemKey, linkItem]) => {
        const item = linkItem as { title: string; linkUrl: string };
        converted[itemKey] = {
          title: item.title,
          linkUrl: item.linkUrl,
        };
      });
      return converted;
    };

    const processColumnEntries = (column: Column): Record<string, MenuItem> => {
      const menuItems: Record<string, MenuItem> = {};
      Object.entries(column).forEach(([listKey, listValue]) => {
        if (listKey === 'equalHeightsColumn' || listKey === 'containerTheme')
          return;
        const list = listValue as LinkGroup | undefined;
        if (list && typeof list === 'object' && list.title) {
          const convertedLinkListItems = list.linkListItems
            ? processLinkListItems(
                list.linkListItems as Record<string, unknown>,
              )
            : {};
          menuItems[list.title] = {
            title: list.title,
            linkUrl: list.mainLink,
            linkListItems: convertedLinkListItems,
          };
        }
      });
      return menuItems;
    };

    const processNavigationData = (
      navData: NavigationData,
    ): Record<string, MenuItem> => {
      const menuItems: Record<string, MenuItem> = {};
      Object.keys(navData).forEach((columnKey) => {
        if (!columnKey.startsWith('column')) return;
        const column = navData[columnKey as keyof NavigationData] as
          | Column
          | undefined;
        if (!column || typeof column !== 'object') return;
        Object.assign(menuItems, processColumnEntries(column));
      });
      return menuItems;
    };

    const transformMenuData = (
      data?: Record<string, NavigationItem>,
    ): Record<string, MenuItem> => {
      const transformed: Record<string, MenuItem> = {};
      if (!data) return transformed;

      Object.entries(data).forEach(([key, value]) => {
        const navData = value.hover_navigation || value.hover_navigation_cop;
        if (!navData) return;
        const menuItems = processNavigationData(navData);
        transformed[key] = { title: key, linkListItems: menuItems };
      });

      return transformed;
    };

    return transformMenuData(nav);
  }, [nav]);

  const segmentSwitcherTitle = useMemo(
    () =>
      `Switch to ${personalSegmentTitle ?? 'Personal'} or ${
        businessSegmentLargeBusinessButtonLabel ?? 'Large business'
      }`,
    [personalSegmentTitle, businessSegmentLargeBusinessButtonLabel],
  );

  const segmentSwitcherItems = useMemo<Record<string, EnrichedItem>>(() => {
    const out: Record<string, EnrichedItem> = {};

    if (personalSegmentTitle) {
      out[personalSegmentTitle] = {
        title: personalSegmentTitle,
        linkUrl: personalSegmentLinkUrl,
      };
    }

    if (businessSegmentSmallBusinessTitle) {
      out[businessSegmentSmallBusinessTitle] = {
        title: businessSegmentSmallBusinessTitle,
        linkUrl: businessSegmentSmallBusinessLinkUrl,
        supporting: businessSegmentSmallBusinessMessage,
      };
    }

    if (businessSegmentLargeBusinessTitle) {
      out[businessSegmentLargeBusinessTitle] = {
        title: businessSegmentLargeBusinessTitle,
        linkUrl: businessSegmentLargeBusinessLinkUrl,
        supporting: businessSegmentLargeBusinessMessage,
      };
    }

    return out;
  }, [
    personalSegmentTitle,
    personalSegmentLinkUrl,
    businessSegmentSmallBusinessTitle,
    businessSegmentSmallBusinessLinkUrl,
    businessSegmentSmallBusinessMessage,
    businessSegmentLargeBusinessTitle,
    businessSegmentLargeBusinessLinkUrl,
    businessSegmentLargeBusinessMessage,
  ]);

  const getCurrentMenuItems = (): Record<string, MenuItem> =>
    navigationStack.length === 0
      ? mainMenuItems
      : navigationStack[navigationStack.length - 1].items;

  const getCurrentTitle = (): string =>
    navigationStack.length === 0
      ? ''
      : navigationStack[navigationStack.length - 1].title;

  const getBackButtonText = (): string => {
    if (navigationStack.length === 0) return '';
    if (navigationStack.length === 1) return 'Back to main menu';
    return `Back to ${navigationStack[navigationStack.length - 2].title}`;
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.linkListItems && Object.keys(item.linkListItems).length > 0) {
      setNavigationStack((prev) => [
        ...prev,
        { title: item.title, items: item.linkListItems! },
      ]);
    } else if (item.linkUrl) {
      window.open(item.linkUrl, '_blank');
    }
  };

  const handleItemKeyDown = (e: KeyboardEvent, item: MenuItem) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleItemClick(item);
    }
  };

  const handleBackClick = () => {
    if (navigationStack.length > 0) {
      setNavigationStack((prev) => prev.slice(0, -1));
    }
  };

  const openSegmentSwitcher = () => {
    setNavigationStack((prev) => [
      ...prev,
      {
        title: segmentSwitcherTitle,
        items: segmentSwitcherItems as Record<string, MenuItem>,
      },
    ]);
  };

  const hasSubItems = (item: MenuItem) =>
    item.linkListItems && Object.keys(item.linkListItems).length > 0;

  const currentItems = getCurrentMenuItems();
  const isMainMenu = navigationStack.length === 0;

  return (
    <div
      id="nested-menu-shell"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
      }}
    >
      <div style={{ padding: isMainMenu ? '16px' : '16px 0px' }}>
        {!isMainMenu ? (
          <div className="nested-menu-header">
            <ButtonV2
              buttonStyle="compact"
              icon={CHEVRON_LEFT_ICON}
              iconPosition="beforeText"
              label={getBackButtonText()}
              onClick={handleBackClick}
              size="s"
              type="button"
              ariaLabel="Back to previous menu"
            />
            <VerticalSpace size="8" />
            <Heading level="2" color="brand">
              {getCurrentTitle()}
            </Heading>
          </div>
        ) : (
          <div className="sidebar-brand-logo">
            <BrandLogo color="brand" subBrand="ee" size={40} />
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div
          style={{
            padding: '16px 0px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {Object.entries(currentItems).map(([key, item]) => {
            const enrichedItem = item as EnrichedItem;
            return (
              <button
                key={key}
                className="nested-menu-item"
                onClick={() => handleItemClick(enrichedItem)}
                onKeyDown={(e) => handleItemKeyDown(e, enrichedItem)}
              >
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  <Text size="m">{enrichedItem.title}</Text>
                  {enrichedItem.supporting ? (
                    <Text size="s">{enrichedItem.supporting}</Text>
                  ) : null}
                </div>
                {hasSubItems(enrichedItem) ? (
                  <Icon color="brand" icon={CHEVRON_RIGHT_ICON} size={20} />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
      {isMainMenu && (
        <div
          className="nested-menu-footer"
          style={{
            padding: '16px',
            marginTop: 'auto',
            background: 'rgb(243, 243, 243)',
          }}
        >
          <button
            className="nested-menu-item--cta"
            onClick={openSegmentSwitcher}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openSegmentSwitcher();
              }
            }}
            aria-label={segmentSwitcherTitle}
          >
            <Text size="m" align="left">
              {segmentSwitcherTitle}
            </Text>
            <Icon color="brand" icon={CHEVRON_RIGHT_ICON} size={20} />
          </button>

          <VerticalSpace size="24" />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <UtilityLinks />
          </div>
        </div>
      )}
    </div>
  );
};

export default NestedMenu;
